import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { usePlanLimits } from "@/hooks/use-plan-limits";

interface Props {
  resource: "saved_games" | "active_pools";
  label: string;
}

/**
 * Mostra "X de Y usados" com barra e CTA de upgrade se o usuário estiver
 * próximo ou no limite. Para planos com limite null, não renderiza nada.
 */
export function PlanLimitBadge({ resource, label }: Props) {
  const { plan, usage, limits, loading } = usePlanLimits();
  if (loading) return null;

  const limit =
    resource === "saved_games"
      ? limits?.saved_games_limit ?? null
      : limits?.active_pools_limit ?? null;
  if (limit === null) return null;

  const used = usage[resource];
  const pct = Math.min(100, (used / Math.max(limit, 1)) * 100);
  const reached = used >= limit;
  const near = !reached && used / limit >= 0.8;

  return (
    <Card
      className={`border-lt-border ${
        reached ? "border-destructive/50 bg-destructive/5" : near ? "border-primary/40 bg-primary/5" : "bg-card/40"
      }`}
    >
      <CardContent className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {label} —{" "}
              <span className="font-medium text-foreground">
                {used}/{limit}
              </span>{" "}
              no plano <span className="uppercase tracking-wider">{plan}</span>
            </span>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>
        {(near || reached) && (
          <Button asChild size="sm" variant={reached ? "default" : "outline"} className="shrink-0">
            <Link to="/planos">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              {reached ? "Fazer upgrade" : "Ver planos"}
              <ArrowUpRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}